/**
 * variants/glow/DomainIcons.jsx
 * Bespoke SVG glyphs for Homy's 21 core domain modules in "spatial-bento-glow" variant.
 * Designed for Cockpit telemetry & IoT status with luminescent drop-shadow presence.
 */

import * as React from "react";

function createSvgIcon(name, renderPaths) {
  const Component = React.forwardRef(
    ({ size, width, height, className = "", embedded = false, ...props }, ref) => {
      const dimension = size || width || 24;

      if (embedded) {
        return (
          <g
            ref={ref}
            className={`filter drop-shadow-[0_0_4px_currentColor] ${className}`}
            data-icon={name}
            {...props}
          >
            {renderPaths()}
          </g>
        );
      }

      return (
        <svg
          ref={ref}
          viewBox="0 0 24 24"
          width={dimension}
          height={height || dimension}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`filter drop-shadow-[0_0_4px_currentColor] ${className}`}
          data-icon={name}
          xmlns="http://www.w3.org/2000/svg"
          {...props}
        >
          {renderPaths()}
        </svg>
      );
    }
  );
  Component.displayName = name;
  return Component;
}

// 1. Properties
export const BuildingPropertyIcon = createSvgIcon("BuildingPropertyIcon", () => (
  <>
    <rect x="3.5" y="10.5" width="17" height="11" rx="1.5" fill="currentColor" fillOpacity="0.25" stroke="none" />
    <rect x="2.5" y="2" width="19" height="20" rx="2.5" />
    <path d="M7 6.5h2.5M14.5 6.5H17M7 11h2.5M14.5 11H17" />
    <path d="M9.5 22v-5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v5" />
  </>
));

// 2. Rentals / Leases
export const KeyLeaseIcon = createSvgIcon("KeyLeaseIcon", () => (
  <>
    <circle cx="7.5" cy="13.5" r="5" fill="currentColor" fillOpacity="0.25" stroke="none" />
    <circle cx="7.5" cy="13.5" r="5" />
    <path d="M12.5 13.5H21.5M17.5 13.5v3.5M20.5 13.5v4" />
    <circle cx="7.5" cy="13.5" r="1.6" fill="currentColor" />
  </>
));

// 3. Maintenance
export const WrenchServiceIcon = createSvgIcon("WrenchServiceIcon", () => (
  <>
    <path
      d="M9.3 6.3a1 1 0 0 1 0 1.4l-1.6 1.6a1 1 0 0 1-1.4 0L2.53 5.53a6 6 0 0 0 7.94 7.94l6.91 6.91a2.12 2.12 0 0 0 3-3l-6.91-6.91a6 6 0 0 0-7.94-7.94L9.3 6.3z"
      fill="currentColor"
      fillOpacity="0.25"
      stroke="none"
    />
    <path d="M9.3 6.3a1 1 0 0 1 0 1.4l-1.6 1.6a1 1 0 0 1-1.4 0L2.53 5.53a6 6 0 0 0 7.94 7.94l6.91 6.91a2.12 2.12 0 0 0 3-3l-6.91-6.91a6 6 0 0 0-7.94-7.94L9.3 6.3z" />
  </>
));

// 4. Payments
export const CreditCardPaymentIcon = createSvgIcon("CreditCardPaymentIcon", () => (
  <>
    <rect x="2" y="9.5" width="20" height="11" rx="2" fill="currentColor" fillOpacity="0.25" stroke="none" />
    <rect x="2" y="3.5" width="20" height="17" rx="2.5" />
    <path d="M2 8.5h20" />
    <rect x="5.5" y="12.5" width="4.2" height="3.5" rx="0.8" />
    <path d="M13.5 14.5h5" />
  </>
));

// 5. Calendar / Visits
export const CalendarClockIcon = createSvgIcon("CalendarClockIcon", () => (
  <>
    <rect x="2" y="3.5" width="20" height="5.5" rx="2" fill="currentColor" fillOpacity="0.25" stroke="none" />
    <rect x="2" y="3.5" width="20" height="18" rx="2.5" />
    <path d="M15 1.5v4M7.5 1.5v4M2 9h20" />
    <circle cx="7.5" cy="14" r="1.2" fill="currentColor" />
    <circle cx="12" cy="14" r="1.2" fill="currentColor" />
    <circle cx="7.5" cy="18" r="1.2" fill="currentColor" />
    <path d="M15.5 18v-2.5H18" />
  </>
));

// 6. Tasks
export const CheckSquareTaskIcon = createSvgIcon("CheckSquareTaskIcon", () => (
  <>
    <rect x="2.5" y="2.5" width="19" height="19" rx="3.5" fill="currentColor" fillOpacity="0.25" stroke="none" />
    <rect x="2.5" y="2.5" width="19" height="19" rx="3.5" />
    <path d="M7.5 12.5l3 3 6-6.5" />
  </>
));

// 7. Support
export const LifebuoySupportIcon = createSvgIcon("LifebuoySupportIcon", () => (
  <>
    <circle cx="12" cy="12" r="10" fill="currentColor" fillOpacity="0.25" stroke="none" />
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="4.5" />
    <path d="M4.9 4.9l3.9 3.9M15.2 15.2l3.9 3.9M19.1 4.9l-3.9 3.9M8.8 15.2l-3.9 3.9" />
  </>
));

// 8. Applications
export const ClipboardDossierIcon = createSvgIcon("ClipboardDossierIcon", () => (
  <>
    <rect x="4" y="7" width="16" height="14.5" rx="2" fill="currentColor" fillOpacity="0.25" stroke="none" />
    <rect x="3" y="4" width="18" height="18" rx="2.5" />
    <path d="M8.5 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v1.5h-7V4z" />
    <path d="M7.5 11h9M7.5 15.5h6" />
  </>
));

// 9. HomyTrust
export const ShieldTrustIcon = createSvgIcon("ShieldTrustIcon", () => (
  <>
    <path
      d="M12 22.5s9-4.2 9-10.8V4.5L12 1.5 3 4.5v7.2c0 6.6 9 10.8 9 10.8z"
      fill="currentColor"
      fillOpacity="0.25"
      stroke="none"
    />
    <path d="M12 22.5s9-4.2 9-10.8V4.5L12 1.5 3 4.5v7.2c0 6.6 9 10.8 9 10.8z" />
    <path d="M8.5 12l2.5 2.5 5-5" />
  </>
));

// 10. HomySign
export const FileSignatureIcon = createSvgIcon("FileSignatureIcon", () => (
  <>
    <path d="M14.5 1.5H5.5a2.5 2.5 0 0 0-2.5 2.5v16a2.5 2.5 0 0 0 2.5 2.5h13a2.5 2.5 0 0 0 2.5-2.5V8l-6.5-6.5z" fill="currentColor" fillOpacity="0.25" stroke="none" />
    <path d="M14.5 1.5H5.5a2.5 2.5 0 0 0-2.5 2.5v16a2.5 2.5 0 0 0 2.5 2.5h13a2.5 2.5 0 0 0 2.5-2.5V8l-6.5-6.5z" />
    <path d="M14.5 1.5V8H21" />
    <path d="M7.5 16.5c1.8-1.2 3.5 1.2 4.8 0s2.4-1.2 3.7 0" />
  </>
));

// 11. Inbox
export const InboxCockpitIcon = createSvgIcon("InboxCockpitIcon", () => (
  <>
    <path d="M2.5 13h4.8l1.7 3h6l1.7-3h4.8v6.5a2.5 2.5 0 0 1-2.5 2.5H5a2.5 2.5 0 0 1-2.5-2.5V13z" fill="currentColor" fillOpacity="0.25" stroke="none" />
    <path d="M21.5 13h-4.8l-1.7 3H9l-1.7-3H2.5V5.5A2.5 2.5 0 0 1 5 3h14a2.5 2.5 0 0 1 2.5 2.5V13z" />
    <path d="M2.5 13v6.5A2.5 2.5 0 0 0 5 22h14a2.5 2.5 0 0 0 2.5-2.5V13" />
    <circle cx="12" cy="8" r="1.75" fill="currentColor" />
  </>
));

// 12. Listings
export const LayoutGridListingsIcon = createSvgIcon("LayoutGridListingsIcon", () => (
  <>
    <rect x="2" y="2" width="8.5" height="8.5" rx="2" fill="currentColor" fillOpacity="0.25" stroke="none" />
    <rect x="2" y="2" width="8.5" height="8.5" rx="2" />
    <rect x="13.5" y="2" width="8.5" height="8.5" rx="2" />
    <rect x="2" y="13.5" width="8.5" height="8.5" rx="2" />
    <rect x="13.5" y="13.5" width="8.5" height="8.5" rx="2" />
  </>
));

// 13. Marketplace
export const StoreMarketplaceIcon = createSvgIcon("StoreMarketplaceIcon", () => (
  <>
    <path d="M2 8.5L4 3h16l2 5.5v2a2.5 2.5 0 0 1-5 0 2.5 2.5 0 0 1-5 0 2.5 2.5 0 0 1-5 0 2.5 2.5 0 0 1-5 0v-2z" fill="currentColor" fillOpacity="0.25" stroke="none" />
    <path d="M2 8.5L4 3h16l2 5.5v2a2.5 2.5 0 0 1-5 0 2.5 2.5 0 0 1-5 0 2.5 2.5 0 0 1-5 0 2.5 2.5 0 0 1-5 0v-2z" />
    <path d="M3.5 11v8.5A2.5 2.5 0 0 0 6 22h12a2.5 2.5 0 0 0 2.5-2.5V11" />
    <path d="M9.5 22v-6h5v6" />
  </>
));

// 14. Memberships
export const CrownTierIcon = createSvgIcon("CrownTierIcon", () => (
  <>
    <path d="M3 18L2 6l5.5 4.5L12 3l4.5 7.5L22 6l-1 12H3z" fill="currentColor" fillOpacity="0.25" stroke="none" />
    <path d="M3 18L2 6l5.5 4.5L12 3l4.5 7.5L22 6l-1 12H3z" />
    <path d="M3 18h18v2.5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V18z" />
    <circle cx="12" cy="13.5" r="1.25" fill="currentColor" />
  </>
));

// 15. Organizations
export const BuildingOrgIcon = createSvgIcon("BuildingOrgIcon", () => (
  <>
    <rect x="13" y="6" width="9" height="16" rx="1.8" fill="currentColor" fillOpacity="0.25" stroke="none" />
    <rect x="2" y="2" width="9" height="20" rx="1.8" />
    <rect x="13" y="6" width="9" height="16" rx="1.8" />
    <path d="M11 10.5h2M11 15h2" />
    <path d="M5.5 6.5h2M5.5 11h2M5.5 15.5h2M16.5 11h2M16.5 15.5h2" />
  </>
));

// 16. Databases
export const DatabaseStackIcon = createSvgIcon("DatabaseStackIcon", () => (
  <>
    <ellipse cx="12" cy="19" rx="9" ry="3.2" fill="currentColor" fillOpacity="0.25" stroke="none" />
    <ellipse cx="12" cy="5" rx="9" ry="3.2" />
    <path d="M3 5v14c0 1.8 4 3.2 9 3.2s9-1.4 9-3.2V5" />
    <path d="M3 12c0 1.8 4 3.2 9 3.2s9-1.4 9-3.2" />
  </>
));

// 17. Comments
export const MessageThreadIcon = createSvgIcon("MessageThreadIcon", () => (
  <>
    <path d="M22 11.5a9.5 9.5 0 0 1-9.5 9.5c-1.7 0-3.3-.4-4.7-1.2L2 21l1.4-5.2A9.5 9.5 0 1 1 22 11.5z" fill="currentColor" fillOpacity="0.25" stroke="none" />
    <path d="M22 11.5a9.5 9.5 0 0 1-9.5 9.5c-1.7 0-3.3-.4-4.7-1.2L2 21l1.4-5.2A9.5 9.5 0 1 1 22 11.5z" />
    <path d="M7.5 12h9M7.5 8h6" />
  </>
));

// 18. Landing Page
export const GlobeWebIcon = createSvgIcon("GlobeWebIcon", () => (
  <>
    <circle cx="12" cy="12" r="10" fill="currentColor" fillOpacity="0.25" stroke="none" />
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" />
    <path d="M12 2a14.5 14.5 0 0 1 0 20M12 2a14.5 14.5 0 0 0 0 20" />
  </>
));

// 19. Settings
export const SlidersSettingsIcon = createSvgIcon("SlidersSettingsIcon", () => (
  <>
    <path d="M2.5 6h19M2.5 12h19M2.5 18h19" />
    <circle cx="8" cy="6" r="2.8" fill="currentColor" fillOpacity="0.25" />
    <circle cx="16" cy="12" r="2.8" fill="currentColor" fillOpacity="0.25" />
    <circle cx="9.5" cy="18" r="2.8" fill="currentColor" fillOpacity="0.25" />
  </>
));

// 20. Core / User
export const UserCockpitIcon = createSvgIcon("UserCockpitIcon", () => (
  <>
    <path d="M12 11.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9z" fill="currentColor" fillOpacity="0.25" stroke="none" />
    <circle cx="12" cy="7" r="4.5" />
    <path d="M4 21.5a8 8 0 0 1 16 0" />
  </>
));

// 21. Public
export const CompassPublicIcon = createSvgIcon("CompassPublicIcon", () => (
  <>
    <circle cx="12" cy="12" r="10" fill="currentColor" fillOpacity="0.25" stroke="none" />
    <circle cx="12" cy="12" r="10" />
    <path d="M16 8l-2.8 7.5-5.2 1.5 2.8-7.5L16 8z" fill="currentColor" />
  </>
));

export const glowDomainGlyphs = {
  properties: BuildingPropertyIcon,
  rentals: KeyLeaseIcon,
  maintenance: WrenchServiceIcon,
  payments: CreditCardPaymentIcon,
  calendar: CalendarClockIcon,
  tasks: CheckSquareTaskIcon,
  support: LifebuoySupportIcon,
  applications: ClipboardDossierIcon,
  trust: ShieldTrustIcon,
  homysign: FileSignatureIcon,
  inbox: InboxCockpitIcon,
  listings: LayoutGridListingsIcon,
  marketplace: StoreMarketplaceIcon,
  memberships: CrownTierIcon,
  organizations: BuildingOrgIcon,
  databases: DatabaseStackIcon,
  comments: MessageThreadIcon,
  landingPage: GlobeWebIcon,
  settings: SlidersSettingsIcon,
  core: UserCockpitIcon,
  public: CompassPublicIcon,
};
